import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { accountsApi, type LoanSchedule } from '@/api/accounts';
import { ErrorState } from '@/components/ErrorState';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { MoneyText } from '@/components/MoneyText';

interface ScheduleTabProps {
  accountId: number;
  currency: string | null;
}

/**
 * LOAN schedule tab (TMMAPP-0008, MM-D-44): payment date, payment, interest, principal, balance
 * after (`xs`: date · payment · balance after), with paid-to-date and computed-payoff lines.
 */
export function ScheduleTab({ accountId, currency }: ScheduleTabProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const md = useMediaQuery(useTheme().breakpoints.up('md'));
  const [schedule, setSchedule] = useState<LoanSchedule | null>(null);
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null);
  const started = useRef(false);

  const load = useCallback(async () => {
    try {
      setSchedule(await accountsApi.loanSchedule(accountId));
      setLoadFailure(null);
    } catch (error) {
      setLoadFailure({ error });
    }
  }, [accountId]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load();
  }, [load]);

  if (loadFailure !== null) {
    return (
      <ErrorState
        error={loadFailure.error}
        what={t('errors.what.loanSchedule')}
        onRetry={() => void load()}
      />
    );
  }

  if (schedule === null) {
    return (
      <Stack spacing={1} aria-busy="true">
        <Skeleton variant="rounded" height={36} />
        <Skeleton variant="rounded" height={36} />
      </Stack>
    );
  }

  return (
    <Box>
      <Stack spacing={0.5} sx={{ mb: 2 }}>
        {schedule.paidToDate !== null && (
          <Typography variant="body2">
            {t('accounts.paidToDate', { date: formatCivilDate(schedule.paidToDate, locale) })}
          </Typography>
        )}
        {schedule.computedPayoffOn !== null && (
          <Typography variant="body2">
            {t('accounts.payoffOn', { date: formatCivilDate(schedule.computedPayoffOn, locale) })}
          </Typography>
        )}
      </Stack>
      {schedule.rows.length === 0 ? (
        <Typography variant="body1">{t('accounts.scheduleEmpty')}</Typography>
      ) : (
        <TableContainer>
          <Table size="small" aria-label={t('accounts.tabSchedule')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('accounts.colDate')}</TableCell>
                <TableCell align="right">{t('accounts.colPayment')}</TableCell>
                {md && <TableCell align="right">{t('accounts.colInterest')}</TableCell>}
                {md && <TableCell align="right">{t('accounts.colPrincipal')}</TableCell>}
                <TableCell align="right">{t('accounts.colBalanceAfter')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedule.rows.map((row) => (
                <TableRow key={row.paymentOn}>
                  <TableCell>{formatCivilDate(row.paymentOn, locale)}</TableCell>
                  <TableCell align="right">
                    <MoneyText minor={row.paymentMinor} currency={currency} />
                  </TableCell>
                  {md && (
                    <TableCell align="right">
                      <MoneyText minor={row.interestMinor} currency={currency} />
                    </TableCell>
                  )}
                  {md && (
                    <TableCell align="right">
                      <MoneyText minor={row.principalMinor} currency={currency} />
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <MoneyText minor={row.balanceAfterMinor} currency={currency} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
